/* page-transition.js — Sneak Freak (AphCom) · branded page transition
   The old Sneak Freak logo (blue PNG, the hero shows it in white) centred on an opaque screen, revealed left to right.

   Leaving  : an eligible link click is held for a short, bounded exit (the `duration` setting, 400–1200 ms). The
              cover is opaque BEFORE navigation starts, so the outgoing page can never flash through it.
   Arriving : layout/theme.liquid's head script re-shows the cover before first paint (html.sf-pt-in, from a
              sessionStorage flag written on click AND on pagehide, so Back gets it too), the mark finishes, fade out.
   Never intercepts: modifier/middle clicks, target≠_self, download, rel=external, data-no-transition, hash-only,
   mailto/tel/js, other origins, cart/checkout/account/password/admin paths, links inside forms, the theme editor,
   reduced motion — or a click any other handler has already claimed with preventDefault.
   Fail-safes: navigation is scheduled on a timer that cannot be cancelled by animation errors,
   bfcache pageshow, visibilitychange and an arrival cap — the cover can never stay stuck. */
'use strict';
(function () {
  const root = document.getElementById('sf-page-transition');
  if (!root) return;
  const html = document.documentElement;
  const KEY = 'sf-pt';
  const duration = Math.min(1200, Math.max(400, parseInt(root.dataset.duration, 10) || 800));
  const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const designMode = !!(window.Shopify && window.Shopify.designMode);
  let raf = 0;
  let leaving = false;

  const setFlag = () => { try { sessionStorage.setItem(KEY, String(Date.now())); } catch (e) { /* private mode */ } };
  const clearFlag = () => { try { sessionStorage.removeItem(KEY); } catch (e) { /* private mode */ } };

  function hide() {
    cancelAnimationFrame(raf);
    root.classList.remove('is-writing');
    root.classList.add('is-leaving');
    root.classList.remove('is-active');
    html.classList.remove('sf-pt-in');
    clearFlag();
    setTimeout(() => root.classList.remove('is-leaving'), 320);
  }

  function show() {
    root.classList.remove('is-leaving');
    root.classList.add('is-active');
  }

  /** Write the signature guides at constant pen speed across `total` ms (hero geometry, no libraries). */
  function write(total, done) {
    const guides = Array.from(root.querySelectorAll('.sf-ink'));
    const nib = root.querySelector('.sf-nib');
    if (!guides.length) {
      /* logo image: a CSS wipe of `total` ms (component-page-transition.css). Restart it every time: drop the
         classes, force a reflow, add .is-writing back. */
      cancelAnimationFrame(raf);
      root.classList.remove('is-done', 'is-writing');
      root.style.setProperty('--sf-pt-dur', total + 'ms');
      void root.offsetWidth;
      root.classList.add('is-writing');
      setTimeout(() => { root.classList.remove('is-writing'); root.classList.add('is-done'); if (done) done(); }, total);
      return;
    }
    let lens;
    try { lens = guides.map((g) => g.getTotalLength() || 0); } catch (e) { if (done) done(); return; }
    const sum = lens.reduce((a, b) => a + b, 0) || 1;
    root.classList.remove('is-done');
    guides.forEach((g, i) => { g.style.strokeDasharray = String(lens[i]); g.style.strokeDashoffset = String(lens[i]); g.style.opacity = '0'; });
    const start = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - start) / total);
      let acc = 0;
      for (let i = 0; i < guides.length; i++) {
        const share = lens[i] / sum;
        const local = share > 0 ? Math.max(0, Math.min(1, (p - acc) / share)) : 1;
        acc += share;
        guides[i].style.strokeDashoffset = String(lens[i] * (1 - local));
        if (local > 0) guides[i].style.opacity = '1';
        if (nib && local > 0 && local < 1) {
          try { const pt = guides[i].getPointAtLength(lens[i] * local); nib.setAttribute('cx', pt.x); nib.setAttribute('cy', pt.y); nib.style.opacity = '0.9'; } catch (e) { /* detached */ }
        }
      }
      if (p < 1) { raf = requestAnimationFrame(step); return; }
      if (nib) nib.style.opacity = '0';
      root.classList.add('is-done');
      if (done) done();
    };
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(step);
  }

  /* ---------------------------------------------------------------- arrival
     The signature is written once, on the way OUT. Arriving, the cover is already up (head script) holding the
     FINISHED mark — so the sequence reads as one continuous animation, not two. Reveal as soon as the page is
     parsed, with a cap so a slow response can never hold the screen. */
  function settle() {
    const guides = root.querySelectorAll('.sf-ink');
    guides.forEach((g) => { g.style.strokeDasharray = 'none'; g.style.strokeDashoffset = '0'; g.style.opacity = '1'; });
    const nib = root.querySelector('.sf-nib');
    if (nib) nib.style.opacity = '0';
    root.classList.add('is-done');
  }
  if (html.classList.contains('sf-pt-in')) {
    show();
    settle();
    if (reduced || designMode) hide();
    else {
      setTimeout(hide, 140);
      setTimeout(hide, 1200); /* fail-safe */
    }
  }
  window.addEventListener('pageshow', (e) => { if (e.persisted) hide(); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    /* coming back to a tab that never navigated: release the cover */
    if (!leaving && (root.classList.contains('is-active') || html.classList.contains('sf-pt-in'))) setTimeout(hide, 200);
  });

  if (reduced || designMode) return;

  /* ---------------------------------------------------------------- departure */
  const SKIP_PATH = /^\/(cart|checkout|checkouts|account|customer_authentication|customer_identity|password|admin|apps|tools|challenge)(\/|$|\?)/i;

  function eligible(a, e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null;
    if (!a || (a.target && a.target !== '_self')) return null;
    if (a.hasAttribute('download') || a.hasAttribute('data-no-transition') || /\bexternal\b/.test(a.getAttribute('rel') || '')) return null;
    /* links another script turns into an in-page fetch (facets, load more, drawers) must keep their own handler */
    if (a.matches('[data-load-more], [data-facets-remove], [data-facets-clear], [data-drawer-open], [data-drawer-close], [data-fetch-url]')) return null;
    const href = a.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#' || /^(mailto|tel|sms|javascript):/i.test(href)) return null;
    let url;
    try { url = new URL(a.href, window.location.href); } catch (err) { return null; }
    if (url.origin !== window.location.origin) return null;
    if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return null;
    if (SKIP_PATH.test(url.pathname)) return null;
    if (a.closest('form')) return null;
    return url.href;
  }

  /* The WINDOW in the bubble phase — the last listener a click reaches — not the document in the capture
     phase, which is the first. From the front of the queue e.defaultPrevented is always still false, so every
     link some other script handles in place looked like a navigation: a colour chip on a product card was
     swapped by card-colors.js and the page left 800 ms later anyway — and a drag that brand-explorer.js
     cancels in its own (deeper, so later) capture listener is equally invisible from up here. Deciding last
     lets those handlers speak first;
     one that stops propagation instead simply gets no transition, and the link behaves as a plain link. */
  window.addEventListener('click', (e) => {
    if (leaving) return;
    const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    let target;
    try { target = eligible(a, e); } catch (err) { return; }
    if (!target) return;
    e.preventDefault();
    leaving = true;
    setFlag();
    /* navigation is on its own timer: even if the drawing below throws, the page still changes */
    const exit = duration;
    setTimeout(() => { window.location.assign(target); }, exit);
    setTimeout(() => { if (leaving && document.visibilityState === 'visible') { leaving = false; hide(); } }, exit + 4000);
    try { show(); write(exit); } catch (err) { /* cover may be plain; navigation still happens */ }
  });

  /* Any other way of leaving (Back/Forward, JS navigation, form-less redirects) still gets the arrival cover,
     which is what stops the outgoing page from flashing on the way to the next one. */
  window.addEventListener('pagehide', (e) => { if (!e.persisted) setFlag(); });
})();
